import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IInsurance } from '../constants/types';

/**
 * Resolves patient's insurance from their coverage ID
 * Uses the new coverage-based system where patients have coverages,
 * and each coverage references an insurance.
 * @param coverageId - The coverage ID from patient.personalData.coverage
 * @returns [insurances[], loading, patientInsurance]
 */
export function usePatientInsurance(coverageId: number | null) {
    const currentInvestigation = useSelector((state: any) => 
        state.investigations.currentInvestigation
    );
    
    const insurances = currentInvestigation?.insurances || [];
    const coverages = currentInvestigation?.coverages || [];
    const loading = useSelector((state: any) => state.investigations.loading);
    
    const patientInsurance = useMemo(() => {
        if (!coverageId || !insurances.length || !coverages.length) {
            return null;
        }
        
        // Find the coverage by ID
        const coverage = coverages.find((c: any) => c.id === coverageId);
        if (!coverage || !coverage.insuranceId) {
            return null;
        }
        
        // Find the insurance by the coverage's insuranceId
        const insurance = insurances.find((ins: IInsurance) => ins.id === coverage.insuranceId);
        return insurance || null;
        
    }, [coverageId, insurances, coverages]);

    return [insurances, loading, patientInsurance] as const;
}

/**
 * Gets the list of insurances from currentInvestigation
 * Use this when you only need the insurances list, not patient-specific data
 * @returns [insurances[], loading]
 */
export function useInsurancesList() {
    const currentInvestigation = useSelector((state: any) => 
        state.investigations.currentInvestigation
    );
    
    const insurances = currentInvestigation?.insurances || [];
    const loading = useSelector((state: any) => state.investigations.loading);
    
    return [insurances, loading] as const;
}

/**
 * Gets the list of coverages from currentInvestigation
 * Use this when you need to display or select coverages
 * @returns [coverages[], loading]
 */
export function useCoveragesList() {
    const currentInvestigation = useSelector((state: any) => 
        state.investigations.currentInvestigation
    );
    
    const coverages = currentInvestigation?.coverages || [];
    const loading = useSelector((state: any) => state.investigations.loading);
    
    return [coverages, loading] as const;
}

/**
 * The coverages a patient may be put on, in one place:
 * - SHER-934: only active coverages — the old percentage bands stay in the list as inactive rows
 *   because old bills resolve them by id, but they are never a choice;
 * - SHER-714: not the coverage of a deactivated insurer (the API also refuses the move).
 */
export function selectableCoverages<C extends { active?: boolean; insuranceId?: number }>(
    coverages: C[],
    insurances: { id: number; active?: boolean }[],
): C[] {
    const inactiveInsurerIds = new Set(insurances.filter((i) => i.active === false).map((i) => i.id));
    return coverages.filter((c) => c.active !== false && !(c.insuranceId != null && inactiveInsurerIds.has(c.insuranceId)));
}

/** The coverages a selector may offer, from currentInvestigation (see selectableCoverages). */
export function useSelectableCoverages() {
    const [coverages, loading] = useCoveragesList();
    const [insurances] = useInsurancesList();
    const selectable = useMemo(() => selectableCoverages(coverages, insurances), [coverages, insurances]);
    return [selectable, loading] as const;
}

/**
 * Gets a specific coverage object and its associated insurance
 * @param coverageId - The coverage ID from patient.personalData.coverage
 * @returns [coverage, insurance, loading]
 */
export function useCoverage(coverageId: number | null) {
    const currentInvestigation = useSelector((state: any) => 
        state.investigations.currentInvestigation
    );
    
    const insurances = currentInvestigation?.insurances || [];
    const coverages = currentInvestigation?.coverages || [];
    const loading = useSelector((state: any) => state.investigations.loading);
    
    const result = useMemo(() => {
        if (!coverageId || !coverages.length) {
            return [null, null];
        }
        
        const coverage = coverages.find((c: any) => c.id === coverageId);
        if (!coverage) {
            return [null, null];
        }
        
        const insurance = insurances.find((ins: IInsurance) => ins.id === coverage.insuranceId);
        return [coverage, insurance];
        
    }, [coverageId, insurances, coverages]);

    return [...result, loading] as const;
}

